import React from 'react';
import PropTypes from 'prop-types';

/**
 * HistoryDetailSidebar Component
 * Displays search history details with images and results in a sidebar
 */
const HistoryDetailSidebar = ({ historyItem, isOpen, onClose, onProductClick }) => {
  // Lock body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!historyItem) return null;

  const { query, results, metadata, createdAt } = historyItem;
  const resultsCount = metadata?.totalResults || results?.length || 0;

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Styles
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? 'visible' : 'hidden',
      transition: 'opacity 0.3s ease, visibility 0.3s ease',
      cursor: 'pointer',
    },
    sidebar: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '600px',
      maxWidth: '90vw',
      backgroundColor: '#ffffff',
      boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.2)',
      zIndex: 1000,
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease',
      overflowY: 'auto',
      cursor: 'default',
    },
    closeButton: {
      position: 'sticky',
      top: '20px',
      left: 'calc(100% - 52px)',
      width: '32px',
      height: '32px',
      border: 'none',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      transition: 'background-color 0.2s ease',
      zIndex: 1,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      marginBottom: '-32px',
    },
    closeIcon: {
      fontSize: '20px',
      color: '#6b7280',
    },
    content: {
      padding: '32px',
    },
    header: {
      marginBottom: '24px',
    },
    title: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0 0 8px 0',
    },
    date: {
      fontSize: '14px',
      color: '#6b7280',
      margin: 0,
    },
    imagesSection: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '12px',
    },
    searchedImageContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px',
    },
    imageWrapper: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      width: '80px',
      height: '80px',
      minWidth: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    imagePlaceholder: {
      width: '100%',
      height: '100%',
      backgroundColor: '#d1d5db',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#9ca3af',
      fontSize: '14px',
    },
    imageLabel: {
      fontSize: '13px',
      color: '#374151',
      fontWeight: '600',
    },
    metaInfo: {
      display: 'flex',
      gap: '16px',
      padding: '12px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      fontSize: '13px',
      color: '#374151',
    },
    metaItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    metaLabel: {
      fontSize: '11px',
      color: '#6b7280',
      textTransform: 'uppercase',
      fontWeight: '600',
      letterSpacing: '0.3px',
    },
    metaValue: {
      fontSize: '13px',
      color: '#1f2937',
      fontWeight: '600',
    },
    resultsSection: {
      marginBottom: '32px',
    },
    resultsGrid: {
      display: 'grid',
      gap: '12px',
    },
    resultCard: {
      display: 'flex',
      gap: '12px',
      padding: '12px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    resultCardHover: {
      backgroundColor: '#f3f4f6',
      borderColor: '#d1d5db',
      transform: 'translateX(-2px)',
    },
    resultImage: {
      width: '80px',
      height: '80px',
      minWidth: '80px',
      backgroundColor: '#ffffff',
      borderRadius: '6px',
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    resultImageImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    resultDetails: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0,
    },
    resultSku: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    resultCategory: {
      fontSize: '12px',
      color: '#6b7280',
    },
    resultConfidence: {
      fontSize: '12px',
      color: '#059669',
      fontWeight: '600',
      marginTop: 'auto',
    },
  };

  return (
    <>
      {/* Overlay */}
      <div style={styles.overlay} onClick={onClose} />

      {/* Sidebar */}
      <div style={styles.sidebar} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          style={styles.closeButton}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
          }}
          aria-label="Close sidebar"
        >
          <span style={styles.closeIcon}>✕</span>
        </button>

        {/* Content */}
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <h2 style={styles.title}>Search Details</h2>
            <p style={styles.date}>{formatDate(createdAt)}</p>
          </div>

          {/* Images Section */}
          <div style={styles.imagesSection}>
            <h3 style={styles.sectionTitle}>Searched Image</h3>
            {(() => {
              // Determine which image was actually used for the search
              let searchedImageUrl = null;
              let imageLabel = 'Original';

              // Check if we have the searchedImageType field (new format)
              if (query?.searchedImageType) {
                const type = query.searchedImageType;

                if (type === 'original' && query?.originalImageUrl) {
                  searchedImageUrl = query.originalImageUrl;
                  imageLabel = 'Original';
                } else if (type === 'enhanced' && query?.enhancedImageUrl) {
                  searchedImageUrl = query.enhancedImageUrl;
                  imageLabel = 'Enhanced';
                } else if (type === 'backgroundRemoved' && query?.backgroundRemovedImageUrl) {
                  searchedImageUrl = query.backgroundRemovedImageUrl;
                  imageLabel = 'Background Removed';
                }
              } else {
                // Fallback logic for old history entries without searchedImageType
                // Priority: backgroundRemoved > enhanced > original > legacy imageUrl
                if (query?.backgroundRemovedImageUrl) {
                  searchedImageUrl = query.backgroundRemovedImageUrl;
                  imageLabel = 'Background Removed';
                } else if (query?.enhancedImageUrl) {
                  searchedImageUrl = query.enhancedImageUrl;
                  imageLabel = 'Enhanced';
                } else if (query?.originalImageUrl) {
                  searchedImageUrl = query.originalImageUrl;
                  imageLabel = 'Original';
                } else if (query?.imageUrl) {
                  // Legacy field
                  searchedImageUrl = query.imageUrl;
                  if (query?.imageProcessing?.enhanced && query?.imageProcessing?.backgroundRemoved) {
                    imageLabel = 'Enhanced + Background Removed';
                  } else if (query?.imageProcessing?.enhanced) {
                    imageLabel = 'Enhanced';
                  } else if (query?.imageProcessing?.backgroundRemoved) {
                    imageLabel = 'Background Removed';
                  }
                }
              }

              return searchedImageUrl ? (
                <div style={styles.searchedImageContainer}>
                  <div style={styles.imageWrapper}>
                    <img src={searchedImageUrl} alt="Searched image" style={styles.image} />
                  </div>
                  <div>
                    <div style={styles.imageLabel}>{imageLabel}</div>
                    {query?.filename && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {query.filename}
                      </div>
                    )}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Meta Information */}
            <div style={styles.metaInfo}>
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Results</span>
                <span style={styles.metaValue}>{resultsCount}</span>
              </div>
              {metadata?.processingTimeMs && (
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Processing Time</span>
                  <span style={styles.metaValue}>{metadata.processingTimeMs}ms</span>
                </div>
              )}
              {query?.fileSize && (
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>File Size</span>
                  <span style={styles.metaValue}>
                    {(query.fileSize / 1024).toFixed(1)} KB
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div style={styles.resultsSection}>
            <h3 style={styles.sectionTitle}>Results ({resultsCount})</h3>
            <div style={styles.resultsGrid}>
              {results && results.map((result, index) => (
                <div
                  key={result.sku || index}
                  style={styles.resultCard}
                  onClick={() => onProductClick && onProductClick(result)}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, styles.resultCardHover);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = styles.resultCard.backgroundColor;
                    e.currentTarget.style.borderColor = styles.resultCard.borderColor;
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  {/* Result Image */}
                  <div style={styles.resultImage}>
                    {result.imageUrl ? (
                      <img src={result.imageUrl} alt={result.sku} style={styles.resultImageImg} />
                    ) : (
                      <div style={styles.imagePlaceholder}>No image</div>
                    )}
                  </div>

                  {/* Result Details */}
                  <div style={styles.resultDetails}>
                    <div style={styles.resultSku}>{result.sku}</div>
                    {result.category && (
                      <div style={styles.resultCategory}>{result.category}</div>
                    )}
                    {result.name && (
                      <div style={styles.resultCategory}>{result.name}</div>
                    )}
                    {result.similarity !== undefined && (
                      <div style={styles.resultConfidence}>
                        {(result.similarity * 100).toFixed(1)}% match
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

HistoryDetailSidebar.propTypes = {
  historyItem: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProductClick: PropTypes.func,
};

export default HistoryDetailSidebar;
